/* eslint-disable react/prop-types */
import React, { ChangeEvent, useState } from 'react';

// import { TagAnnotationData } from '../page-data/tag-annotations-data';
import { ProblemStage, problemStageValue } from '../common/page-info';
import { TagScenario } from '../common-components/tag-scenario';
import { getImageUrlFromScenario } from '../common/helpers';
import { EvalTextField } from '../common-components/eval-text-field';
import { EvalData } from '../page-data/eval-data';
import { EvalTags } from '../common-components/eval-tags';
import { Button, Grid, Paper } from '@mui/material';
import { noOp } from '../utils/utils';
import { Discourse } from '../common-components/discourse';
import { PageComp } from './page-comp-props';

//------------------------------------------------------------------------------
const SelfEval: PageComp = (props) => {
  const { isTeacherView, stage, onChange = noOp, showSecondResponse } = props;
  const pageData = props.pageData as EvalData;
  const { scenario, skill, customQuestionPrompt } = pageData;
  const skillName = skill.name.toLocaleLowerCase();
  const name = scenario?.metadata?.patient?.name;

  const [showFirstReponse, setShowFirstResponse] = useState<boolean>(false);
  const showFirstReponseButtonText = (showFirstReponse) ?
    'Hide Traits' :
    'Review Traits';

  // Thank you for your response.
  // Remember to always consider the key traits that should be included as well as those to avoid in your communication with patients.

  const instruction = customQuestionPrompt ?
    (
      <div>
        <b>Read the dialogue</b> between the provider and {name}. {customQuestionPrompt}.
      </div>
    ) : 
    (
      <div>
        <b>Read the dialogue</b> between the provider and {name}. Think about how
        you would respond to {name} using the competency of <b>{skillName}</b>.
      </div>
    );

  const isProblemStageReached = (stageEval: ProblemStage) =>
    problemStageValue(stage) >= problemStageValue(stageEval);
  const isAtProblemStage = (stageEval: ProblemStage) =>
    problemStageValue(stage) == problemStageValue(stageEval);

  const onEvalChange = () => {
    onChange();
  };

  // OK for Assessing Suicide Risk, it should read as we said "At this point in your sequential assessment, you should avoid..."
  // But for Assigning HW and Reviewing HW, we should say "At this point in your sequence of strategies, you should avoid..."
  let customDescription = 'assessment';
  const skillId = skill.id;
  if (skillId === 'Assessing Suicide Risk') {
    customDescription = 'sequential assessment';
  } else if (skillId === 'Assigning Homework' || skillId === 'Reviewing Homework') {
    customDescription = 'sequence of strategies';
  } else {
    customDescription = 'intervention';
  }

  let tagInstructions = `At this point in your ${customDescription}, you should avoid the negative traits
    listed below while also including the positive ones. Evaluate your response for each trait.`;

  const isAtFirstAttempt = isAtProblemStage(ProblemStage.CollectInput);
  const isAtSecondAttempt = isAtProblemStage(ProblemStage.SecondTry) && showSecondResponse;
  const isAtEvaluate = isAtProblemStage(ProblemStage.Evaluate);
  const reachedEnd = isProblemStageReached(ProblemStage.ShowResult);
  const showFreeResponseField = isProblemStageReached(ProblemStage.CollectInput);
  if (isAtSecondAttempt) {
    tagInstructions = `Your first response was: ${pageData.firstResponse}`;
  }

  let freeResponseInstruction = 'Enter Free Response Here';
  if (!customQuestionPrompt && isAtFirstAttempt) {
    freeResponseInstruction = `In few sentences, write what you would say to ${name} next.`;
  } else if (isAtSecondAttempt) {
    freeResponseInstruction = 'Rewrite your response so it properly includes/excludes the traits listed previously.';
  }

  let freeResponseInitialValue = pageData.firstResponse;
  if (reachedEnd) {
    freeResponseInitialValue = pageData.secondResponse;
  }

  const freeResponseField = showFreeResponseField ? EvalFreeResponseTextField({
    disabled: !(isAtFirstAttempt || isAtSecondAttempt),
    instruction: freeResponseInstruction,
    initialValue: freeResponseInitialValue,
    changeCallback: (value) => {
      onChange();
      if (isAtFirstAttempt) {
        pageData.firstResponse = value;
      } else if (isAtSecondAttempt) {
        pageData.secondResponse = value;
      }
    }
  }) : null;

  const evaluateTagsSection =
    isAtEvaluate || showFirstReponse ? (
      <Grid item>
        <EvalTags
          instructions={tagInstructions}
          pageData={pageData}
          onChange={onEvalChange}
          disabled={!isAtEvaluate}
        />
      </Grid>
    ) : null;

  const showFirstResponseComp = isProblemStageReached(ProblemStage.SecondTry) ? <Button
    sx={{
      backgroundColor: 'var(--Action-Background)',
      color: 'black',
      borderRadius: '0 5px 4px 0',
    }}
    onClick={() => {
    setShowFirstResponse(!showFirstReponse)
  }}>{showFirstReponseButtonText}</Button> : null;

  return (
    <Grid spacing='4' container direction='column'>
      <Grid item>
        <TagScenario
          imgUrl={getImageUrlFromScenario(scenario)}
          background={scenario?.metadata.patient.background}
          content={scenario?.content?.markdown || scenario?.content.text || ''}
          description={instruction}
        />
      </Grid>
      <Grid item>
        <Paper>
          <Discourse
            isTeacherView={!!isTeacherView}
            onReviewed={props.onReviewed}
            pageData={pageData}
            problemStage={stage}
          />
        </Paper>
      </Grid>
      {freeResponseField}
      {showFirstResponseComp}
      {evaluateTagsSection}
    </Grid>
  );
};

type ResponseTextFieldProps = {
  disabled: boolean;
  instruction: string;
  changeCallback: (value: string) => void;
  initialValue: string;
};

const EvalFreeResponseTextField = ({
  disabled,
  instruction,
  changeCallback,
  initialValue
}: ResponseTextFieldProps) => {
  return <Grid item>
    <EvalTextField
      disabled={disabled}
      label='Provider Response'
      instructions={instruction}
      value={initialValue}
      onChange={(event: ChangeEvent<HTMLInputElement>) => {
        changeCallback(event.target.value);
      }}
    />
  </Grid>
};

//******************************************************************************
export const SelfEvalComp = SelfEval;
